import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { motion } from 'framer-motion'
import {
  User,
  Bell,
  Link as LinkIcon,
  Save,
  Camera,
  Lock,
  Users,
  CreditCard,
  Check,
  ExternalLink,
  Trash2,
  AlertTriangle,
  RefreshCw,
  Award,
  Sparkles,
  ArrowLeft,
  VideoIcon,
  Slack,
} from 'lucide-react'
import Sidebar from '../components/layout/Sidebar'
import { useZoomOAuth } from '../context/ZoomOAuthContext'
import BadgesList from '../components/badges/BadgesList'
import AllBadgesTooltip from '../components/badges/AllBadgesTooltip'
import { useAuth } from '../context/AuthContext'
import { useOrg } from '../context/OrgContext'
import { authAPI, calendarAPI, orgAPI, badgesAPI, teamsAPI, stripeAPI } from '../services/api'
import SlackHuddleConnect from '../components/SlackHuddleConnect'
import ZoomAutoCaptureToggle from '../components/ZoomAutoCaptureToggle'
import { useToast } from '../components/common/Toast'
import { useBilling } from '../context/BillingContext'
import { getAllTimezoneOptions } from '../utils/timezoneLabels'
import { RITHMO_TTS_VOICE_OPTIONS } from '@core/tts/rithmoTtsVoices'
import { clearTtsProfileClientCache } from '../services/ttsProfileClient'
import { decodeHtmlEntities } from '../utils/displayText'

function getDetectedTimezone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || ''
  } catch {
    return ''
  }
}

const TEAL = '#16c1b2'
const TEAL_GRADIENT = 'linear-gradient(135deg, #16c1b2 0%, #0b6fa4 100%)'

function SettingsPage() {
  const [searchParams] = useSearchParams()
  const standalone = searchParams.get('standalone') === 'true'
  const { user, updateUser } = useAuth()
  const { isConnected: zoomConnected, connectZoomUrl } = useZoomOAuth()
  const toast = useToast()
  const timezoneOptions = useMemo(() => getAllTimezoneOptions(), [])
  const { org, loading: orgLoading, isAdmin, refetchOrg, allOrgs, switchOrg } = useOrg()
  const [switchingOrg, setSwitchingOrg] = useState(false)
  const [activeSection, setActiveSection] = useState(() => {
    const params = new URLSearchParams(window.location.search)
    return params.get('tab') || params.get('section') || 'profile'
  })
  const [currentPlan, setCurrentPlan] = useState('free')
  const [planLoading, setPlanLoading] = useState(false)
  const [checkoutLoading, setCheckoutLoading] = useState(null)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [passwordSaving, setPasswordSaving] = useState(false)
  const [passwordSaved, setPasswordSaved] = useState(false)
  const [passwordError, setPasswordError] = useState('')
  const fileInputRef = useRef(null)

  const [profile, setProfile] = useState({
    name: user?.name || '',
    email: user?.email || '',
    avatar: user?.avatar || '',
    timezone: user?.timezone || getDetectedTimezone() || '',
  })

  const [passwordForm, setPasswordForm] = useState({
    current: '',
    next: '',
    confirm: '',
  })
  const [orgMembers, setOrgMembers] = useState([])
  const [pendingInvitations, setPendingInvitations] = useState([])
  const [membersLoading, setMembersLoading] = useState(false)
  const [orgActionError, setOrgActionError] = useState('')
  const [inviteForm, setInviteForm] = useState({ email: '' })
  const [inviteSuccess, setInviteSuccess] = useState('')
  const [inviteError, setInviteError] = useState('')
  const [inviting, setInviting] = useState(false)
  const [inviteeNotice, setInviteeNotice] = useState('')
  const [reminderSendingId, setReminderSendingId] = useState(null)
  const [revokingInviteId, setRevokingInviteId] = useState(null)
  const [orgName, setOrgName] = useState('')
  const [creatingOrg, setCreatingOrg] = useState(false)
  const [createOrgError, setCreateOrgError] = useState('')
  const [profileError, setProfileError] = useState('')

  const [notifications, setNotifications] = useState({
    meetingReminders: true,
    weeklyReport: true,
    postMeetingSummary: true,
    marketingEmails: false,
    meetingInvitations: true,
    dailyDigest: true,
  })
  const [notificationsLoading, setNotificationsLoading] = useState(false)
  const [notificationsSaving, setNotificationsSaving] = useState(false)
  const [notificationsSaved, setNotificationsSaved] = useState(false)
  const [notificationsError, setNotificationsError] = useState('')

  const [calendarStatus, setCalendarStatus] = useState({
    google: { isConnected: false, connectedEmail: '', lastSyncAt: '', lastError: '' },
    microsoft: { isConnected: false, connectedEmail: '', lastSyncAt: '', lastError: '' },
  })
  const [calendarLoading, setCalendarLoading] = useState(false)
  const [calendarError, setCalendarError] = useState('')
  const [calendarConnecting, setCalendarConnecting] = useState({ google: false, microsoft: false })
  const [calendarSyncing, setCalendarSyncing] = useState({ google: false, microsoft: false })
  const [slackStatus, setSlackStatus] = useState({ available: true, connected: false, slackUserId: '', linkedAt: '' })
  const [slackConnecting, setSlackConnecting] = useState(false)

  const [profileBadges, setProfileBadges] = useState([])

  const [rithmoTtsVoice, setRithmoTtsVoice] = useState('cordelia')
  const [customizationSaving, setCustomizationSaving] = useState(false)
  const [customizationSaved, setCustomizationSaved] = useState(false)
  const [customizationError, setCustomizationError] = useState('')

  const [teams, setTeams] = useState([])
  const [teamsLoading, setTeamsLoading] = useState(false)
  const [newTeamName, setNewTeamName] = useState('')
  const [newTeamColor, setNewTeamColor] = useState('#64748b')
  const [creatingTeam, setCreatingTeam] = useState(false)
  const [createTeamError, setCreateTeamError] = useState('')
  const [expandedTeamId, setExpandedTeamId] = useState(null)
  const [teamDetail, setTeamDetail] = useState(null)
  const [addMemberTeamId, setAddMemberTeamId] = useState(null)
  const [addMemberUserId, setAddMemberUserId] = useState('')
  const [teamActionError, setTeamActionError] = useState('')

  const sections = useMemo(() => {
    return [
      { id: 'profile', label: 'Profile', icon: <User className="w-5 h-5" /> },
      { id: 'notifications', label: 'Notifications', icon: <Bell className="w-5 h-5" /> },
      // { id: 'rithmo-customization', label: 'Rithmo Customization', icon: <Sparkles className="w-5 h-5" /> },
      { id: 'integrations', label: 'Integrations', icon: <LinkIcon className="w-5 h-5" /> },
      { id: 'organization', label: 'Organization', icon: <Users className="w-5 h-5" /> },
      { id: 'teams', label: 'Teams', icon: <Users className="w-5 h-5" /> },
      ...(isAdmin
        ? [{ id: 'plans', label: 'Plans', icon: <CreditCard className="w-5 h-5" /> }]
        : []),
    ]
  }, [isAdmin])

  useEffect(() => {
    if (!user) return
    setProfile((prev) => ({
      ...prev,
      name: user.name || '',
      email: user.email || '',
      avatar: user.avatar || '',
      timezone: user.timezone || getDetectedTimezone() || '',
    }))
  }, [user])

  useEffect(() => {
    const v = user?.tts_voice
    if (typeof v === 'string' && v.length > 0) {
      setRithmoTtsVoice(v)
    } else {
      setRithmoTtsVoice('cordelia')
    }
  }, [user?.tts_voice])

  useEffect(() => {
    if (!org) {
      setOrgMembers([])
      setPendingInvitations([])
      return
    }
    setMembersLoading(true)
    const membersPromise = isAdmin
      ? orgAPI.getMembers().then((res) => {
          const list = res?.data?.members ?? []
          const currentUserId = user?.id
          return list.map((m) => ({
            id: m.id,
            name: m.name ?? m.full_name ?? m.email,
            email: m.email,
            role: m.role,
            isCurrentUser: String(m.id) === String(currentUserId),
            pending: false,
          }))
        }).catch(() => [])
      : Promise.resolve([])

    const invitationsPromise = isAdmin
      ? orgAPI.getInvitations().then((res) => {
          const list = res?.data?.invitations ?? []
          return list.map((inv) => ({
            id: `invite_${inv.id}`,
            inviteId: inv.id,
            email: inv.email,
            role: inv.role,
            pending: true,
          }))
        }).catch(() => [])
      : Promise.resolve([])

    Promise.all([membersPromise, invitationsPromise]).then(([members, invitations]) => {
      setOrgMembers(members)
      setPendingInvitations(invitations)
    }).finally(() => setMembersLoading(false))
  }, [org, user?.id, isAdmin])

  useEffect(() => {
    if (!sections.find((section) => section.id === activeSection)) {
      setActiveSection(sections[0]?.id || 'profile')
    }
  }, [activeSection, sections])

  useEffect(() => {
    if (activeSection !== 'profile' || !user?.id) return
    badgesAPI.getMyBadges().then((res) => {
      const data = res?.data ?? res
      setProfileBadges(data?.badges ?? [])
    }).catch(() => setProfileBadges([]))
  }, [activeSection, user?.id])

  useEffect(() => {
    if (activeSection !== 'teams') return
    setTeamsLoading(true)
    setTeamActionError('')
    teamsAPI
      .list()
      .then((res) => {
        const data = res?.data ?? res
        setTeams(data?.teams ?? [])
      })
      .catch(() => setTeams([]))
      .finally(() => setTeamsLoading(false))
  }, [activeSection])


  useEffect(() => {
    if (activeSection !== 'notifications' || !user?.id) return
    setNotificationsLoading(true)
    setNotificationsError('')
    authAPI
      .getNotifications()
      .then((res) => {
        const data = res?.data ?? res
        const prefs = data?.notifications
        if (prefs && typeof prefs === 'object') {
          setNotifications({
            meetingReminders: prefs.meetingReminders ?? true,
            weeklyReport: prefs.weeklyReport ?? true,
            postMeetingSummary: prefs.postMeetingSummary ?? true,
            marketingEmails: prefs.marketingEmails ?? false,
            meetingInvitations: prefs.meetingInvitations ?? true,
            dailyDigest: prefs.dailyDigest ?? true,
          })
        }
      })
      .catch((err) => setNotificationsError(err?.message ?? 'Failed to load preferences.'))
      .finally(() => setNotificationsLoading(false))
  }, [activeSection, user?.id])

  const formatTimestamp = useCallback((value) => {
    if (!value) return 'Never'
    const parsed = new Date(value)
    if (Number.isNaN(parsed.getTime())) return value
    return parsed.toLocaleString()
  }, [])

  const fetchCalendarStatus = useCallback(async () => {
    setCalendarLoading(true)
    setCalendarError('')
    try {
      const response = await calendarAPI.listIntegrations()
      const data = response?.data ?? response
      const next = {
        google: { isConnected: false, connectedEmail: '', lastSyncAt: '', lastError: '' },
        microsoft: { isConnected: false, connectedEmail: '', lastSyncAt: '', lastError: '' },
      }
      if (Array.isArray(data)) {
        data.forEach((entry) => {
          if (entry?.provider && next[entry.provider]) {
            next[entry.provider] = {
              ...next[entry.provider],
              ...entry,
            }
          }
        })
      }
      setCalendarStatus(next)
    } catch (err) {
      setCalendarError('Unable to load calendar integration status.')
    } finally {
      setCalendarLoading(false)
    }
  }, [])

  const fetchSlackStatus = useCallback(async () => {
    try {
      const response = await calendarAPI.slackStatus()
      const data = response?.data ?? response
      if (data && typeof data === 'object') {
        setSlackStatus({
          available: data.available !== false,
          connected: Boolean(data.connected),
          slackUserId: data.slackUserId || '',
          linkedAt: data.linkedAt || '',
        })
      }
    } catch (err) {
      // Non-fatal: leave the card in its default (not connected) state.
    }
  }, [])

  useEffect(() => {
    if (activeSection === 'integrations') {
      fetchCalendarStatus()
      fetchSlackStatus()
    }
  }, [activeSection, fetchCalendarStatus, fetchSlackStatus])

  useEffect(() => {
    const handleFocus = () => {
      if (activeSection === 'integrations') {
        fetchCalendarStatus()
        fetchSlackStatus()
      }
    }
    window.addEventListener('focus', handleFocus)
    return () => window.removeEventListener('focus', handleFocus)
  }, [activeSection, fetchCalendarStatus, fetchSlackStatus])

  const handleCalendarConnect = async (provider) => {
    setCalendarConnecting((prev) => ({ ...prev, [provider]: true }))
    setCalendarError('')
    try {
      const response = provider === 'google'
        ? await calendarAPI.connectGoogle()
        : await calendarAPI.connectMicrosoft()
      const data = response?.data ?? response
      if (data?.authUrl) {
        window.location.href = data.authUrl
      } else {
        setCalendarError('Unable to start OAuth flow.')
      }
    } catch (err) {
      setCalendarError('Unable to start OAuth flow.')
    } finally {
      setCalendarConnecting((prev) => ({ ...prev, [provider]: false }))
    }
  }

  const handleCalendarDisconnect = async (provider) => {
    setCalendarError('')
    try {
      await calendarAPI.disconnect(provider)
      fetchCalendarStatus()
    } catch (err) {
      setCalendarError('Unable to disconnect calendar integration.')
    }
  }

  const handleCalendarSync = async (provider) => {
    setCalendarSyncing((prev) => ({ ...prev, [provider]: true }))
    setCalendarError('')
    try {
      await calendarAPI.sync(provider)
      fetchCalendarStatus()
    } catch (err) {
      setCalendarError('Calendar sync failed.')
    } finally {
      setCalendarSyncing((prev) => ({ ...prev, [provider]: false }))
    }
  }

  const handleSlackConnect = async () => {
    setSlackConnecting(true)
    setCalendarError('')
    try {
      const response = await calendarAPI.connectSlack()
      const data = response?.data ?? response
      if (data?.authUrl) {
        window.location.href = data.authUrl
      } else {
        setCalendarError('Unable to start the Slack connection.')
      }
    } catch (err) {
      setCalendarError('Unable to start the Slack connection.')
    } finally {
      setSlackConnecting(false)
    }
  }

  const handleSave = async () => {
    setSaving(true)
    setSaved(false)
    setProfileError('')
    try {
      const res = await authAPI.updateProfile({
        full_name: profile.name?.trim() || undefined,
        timezone: profile.timezone?.trim() || null,
      })
      const data = res?.data ?? res
      const updated = data?.user
      if (updated) {
        updateUser({
          name: updated.name ?? updated.full_name ?? profile.name,
          email: updated.email ?? profile.email,
          avatar: profile.avatar,
          timezone: updated.timezone ?? profile.timezone,
          ...(updated.tts_voice !== undefined ? { tts_voice: updated.tts_voice } : {}),
        })
      }
      setSaved(true)
      setTimeout(() => setSaved(false), 3000)
    } catch (err) {
      setProfileError(err?.message ?? 'Failed to update profile.')
    } finally {
      setSaving(false)
    }
  }

  const handleAvatarChange = (event) => {
    const file = event.target.files?.[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = () => {
      setProfile((prev) => ({ ...prev, avatar: reader.result }))
    }
    reader.readAsDataURL(file)
  }

  const handleCustomizationSave = async () => {
    setCustomizationError('')
    setCustomizationSaving(true)
    try {
      const res = await authAPI.updateProfile({ tts_voice: rithmoTtsVoice })
      const data = res?.data ?? res
      const updated = data?.user
      if (updated) {
        updateUser({
          tts_voice: updated.tts_voice ?? rithmoTtsVoice,
        })
      }
      clearTtsProfileClientCache()
      setCustomizationSaved(true)
      setTimeout(() => setCustomizationSaved(false), 3000)
    } catch (err) {
      setCustomizationError(err?.message ?? 'Failed to save voice preference.')
    } finally {
      setCustomizationSaving(false)
    }
  }

  const handleNotificationsSave = async () => {
    setNotificationsError('')
    setNotificationsSaving(true)
    try {
      const res = await authAPI.updateNotifications(notifications)
      const data = res?.data ?? res
      const prefs = data?.notifications
      if (prefs && typeof prefs === 'object') {
        setNotifications({
          meetingReminders: prefs.meetingReminders ?? true,
          weeklyReport: prefs.weeklyReport ?? true,
          postMeetingSummary: prefs.postMeetingSummary ?? true,
          marketingEmails: prefs.marketingEmails ?? false,
          meetingInvitations: prefs.meetingInvitations ?? true,
          dailyDigest: prefs.dailyDigest ?? true,
        })
      }
      setNotificationsSaved(true)
      setTimeout(() => setNotificationsSaved(false), 3000)
    } catch (err) {
      setNotificationsError(err?.message ?? 'Failed to save preferences.')
    } finally {
      setNotificationsSaving(false)
    }
  }

  const handlePasswordUpdate = async () => {
    setPasswordError('')
    if (!passwordForm.current || !passwordForm.next || !passwordForm.confirm) {
      setPasswordError('Please fill in all password fields.')
      return
    }
    if (passwordForm.next !== passwordForm.confirm) {
      setPasswordError('New passwords do not match.')
      return
    }
    if (passwordForm.next.length < 8) {
      setPasswordError('New password must be at least 8 characters.')
      return
    }
    setPasswordSaving(true)
    try {
      await authAPI.changePassword({
        old_password: passwordForm.current,
        new_password: passwordForm.next,
        confirm_password: passwordForm.confirm,
      })
      setPasswordSaved(true)
      setPasswordForm({ current: '', next: '', confirm: '' })
      setTimeout(() => setPasswordSaved(false), 3000)
    } catch (err) {
      setPasswordError(err?.message ?? 'Failed to update password.')
    } finally {
      setPasswordSaving(false)
    }
  }

  const handleCreateOrg = async () => {
    setCreateOrgError('')
    if (!orgName.trim()) {
      setCreateOrgError('Enter an organization name.')
      return
    }
    setCreatingOrg(true)
    try {
      await orgAPI.createOrg(orgName.trim())
      await refetchOrg()
      setOrgName('')
    } catch (err) {
      setCreateOrgError(err?.message ?? 'Failed to create organization.')
    } finally {
      setCreatingOrg(false)
    }
  }

  const handleInvite = async () => {
    setInviteSuccess('')
    setInviteError('')
    if (!inviteForm.email.trim()) {
      setInviteError('Please provide an email.')
      return
    }
    setInviting(true)
    try {
      await orgAPI.createInvitation({ email: inviteForm.email.trim(), role: 'member' })
      setInviteForm({ email: '' })
      setInviteSuccess('Invite sent. They will get an email with a link to review and accept the invitation.')
      try {
        const res = await orgAPI.getInvitations()
        const list = res?.data?.invitations ?? []
        setPendingInvitations(list.map((inv) => ({
          id: `invite_${inv.id}`,
          inviteId: inv.id,
          email: inv.email,
          role: inv.role,
          pending: true,
        })))
      } catch { /* non-fatal — list will refresh on next render */ }
    } catch (err) {
      setInviteError(err?.message ?? 'Failed to send invite.')
    } finally {
      setInviting(false)
    }
  }

  const handleSendInviteReminder = async (inviteId, email) => {
    if (!inviteId) return
    setInviteSuccess('')
    setInviteError('')
    setInviteeNotice('')
    setReminderSendingId(inviteId)
    try {
      const res = await orgAPI.resendInvitation(inviteId)
      setInviteSuccess(`Reminder sent to ${email}.`)
      const inv = res?.data?.invitee
      const orgs = inv?.orgs ?? []
      if (inv?.hasAccount && orgs.length > 0) {
        const names = orgs.map((o) => o.name).filter(Boolean)
        setInviteeNotice(
          `This person already has a Rithmo account (currently in: ${names.join(', ')}). ` +
            'If they accept and have no company workspace yet, their active organization will switch to yours.',
        )
      }
      // Refresh the invitation list so the updated expiry is reflected immediately.
      try {
        const listRes = await orgAPI.getInvitations()
        const list = listRes?.data?.invitations ?? []
        setPendingInvitations(list.map((i) => ({
          id: `invite_${i.id}`,
          inviteId: i.id,
          email: i.email,
          role: i.role,
          pending: true,
        })))
      } catch { /* non-fatal */ }
    } catch (err) {
      setInviteError(err?.message ?? 'Failed to send reminder.')
    } finally {
      setReminderSendingId(null)
    }
  }

  const handleRevokeInvitation = async (inviteId) => {
    if (!inviteId) return
    setOrgActionError('')
    setInviteSuccess('')
    setInviteError('')
    setInviteeNotice('')
    setRevokingInviteId(inviteId)
    try {
      await orgAPI.revokeInvitation(inviteId)
      setInviteSuccess('Invitation revoked.')
      const res = await orgAPI.getInvitations()
      const list = res?.data?.invitations ?? []
      setPendingInvitations(
        list.map((inv) => ({
          id: `invite_${inv.id}`,
          inviteId: inv.id,
          email: inv.email,
          role: inv.role,
          pending: true,
        })),
      )
    } catch (err) {
      setInviteError(err?.message ?? 'Failed to revoke invitation.')
    } finally {
      setRevokingInviteId(null)
    }
  }

  const handleRemoveOrgMember = async (userId) => {
    if (!isAdmin || !userId) return
    setOrgActionError('')
    try {
      await orgAPI.removeMember(userId)
      // Fetch the updated list and refetch org context in parallel to avoid a
      // window where the sidebar reflects the new org but the table is stale.
      const [res] = await Promise.all([orgAPI.getMembers(), refetchOrg()])
      const list = res?.data?.members ?? []
      const currentUserId = user?.id
      setOrgMembers(list.map((m) => ({
        id: m.id,
        name: m.name ?? m.full_name ?? m.email,
        email: m.email,
        role: m.role,
        isCurrentUser: String(m.id) === String(currentUserId),
        pending: false,
      })))
    } catch (err) {
      setOrgActionError(err?.message ?? 'Failed to remove member.')
    }
  }

  const handleSetOrgMemberRole = async (userId, role) => {
    if (!isAdmin || !userId) return
    setOrgActionError('')
    try {
      await orgAPI.setMemberRole(userId, { role })
      // Fetch the updated list and refetch org context in parallel.
      const [res] = await Promise.all([orgAPI.getMembers(), refetchOrg()])
      const list = res?.data?.members ?? []
      const currentUserId = user?.id
      setOrgMembers(list.map((m) => ({
        id: m.id,
        name: m.name ?? m.full_name ?? m.email,
        email: m.email,
        role: m.role,
        isCurrentUser: String(m.id) === String(currentUserId),
        pending: false,
      })))
    } catch (err) {
      setOrgActionError(err?.message ?? 'Failed to update role.')
    }
  }

  const handleCreateTeam = async () => {
    setCreateTeamError('')
    if (!newTeamName.trim()) {
      setCreateTeamError('Enter a team name.')
      return
    }
    setCreatingTeam(true)
    try {
      await teamsAPI.create({ name: newTeamName.trim(), color: newTeamColor })
      setNewTeamName('')
      setNewTeamColor('#64748b')
      const res = await teamsAPI.list()
      const data = res?.data ?? res
      setTeams(data?.teams ?? [])
    } catch (err) {
      setCreateTeamError(err?.message ?? 'Failed to create team.')
    } finally {
      setCreatingTeam(false)
    }
  }

  const handleUpdateTeamColor = async (teamId, color) => {
    if (!teamId) return
    try {
      await teamsAPI.update(teamId, { color })
      const res = await teamsAPI.list()
      const data = res?.data ?? res
      setTeams(data?.teams ?? [])
      if (expandedTeamId === teamId) {
        const detail = await teamsAPI.get(teamId)
        setTeamDetail(detail?.data ?? detail)
      }
    } catch (err) {
      setTeamActionError(err?.message ?? 'Failed to update team color.')
    }
  }

  const handleLoadTeamDetail = async (teamId) => {
    setTeamActionError('')
    if (expandedTeamId === teamId && teamDetail?.team?.id === teamId) {
      setExpandedTeamId(null)
      setTeamDetail(null)
      return
    }
    try {
      const res = await teamsAPI.get(teamId)
      const data = res?.data ?? res
      setTeamDetail(data)
      setExpandedTeamId(teamId)
    } catch (err) {
      setTeamActionError(err?.message ?? 'Failed to load team.')
    }
  }

  const handleArchiveTeam = async (teamId) => {
    if (!window.confirm('Archive this team? It will no longer appear in the list.')) return
    setTeamActionError('')
    try {
      await teamsAPI.archive(teamId)
      setTeams((prev) => prev.filter((t) => t.id !== teamId))
      if (expandedTeamId === teamId) setExpandedTeamId(null)
      setTeamDetail(null)
    } catch (err) {
      setTeamActionError(err?.message ?? 'Failed to archive team.')
    }
  }

  const handleAddTeamMember = async () => {
    if (!addMemberTeamId || !addMemberUserId) return
    setTeamActionError('')
    try {
      await teamsAPI.addMember(addMemberTeamId, { userId: addMemberUserId, role: 'member' })
      const res = await teamsAPI.get(addMemberTeamId)
      setTeamDetail(res?.data ?? res)
      setAddMemberUserId('')
      setAddMemberTeamId(null)
    } catch (err) {
      setTeamActionError(err?.message ?? 'Failed to add member.')
    }
  }

  const handleRemoveTeamMember = async (teamId, userId) => {
    setTeamActionError('')
    try {
      await teamsAPI.removeMember(teamId, userId)
      const res = await teamsAPI.get(teamId)
      setTeamDetail(res?.data ?? res)
      const resList = await teamsAPI.list()
      setTeams((resList?.data ?? resList)?.teams ?? [])
    } catch (err) {
      setTeamActionError(err?.message ?? 'Failed to remove member.')
    }
  }

  const handleSetTeamMemberRole = async (teamId, userId, role) => {
    setTeamActionError('')
    try {
      await teamsAPI.setMemberRole(teamId, userId, { role })
      const res = await teamsAPI.get(teamId)
      setTeamDetail(res?.data ?? res)
    } catch (err) {
      setTeamActionError(err?.message ?? 'Failed to update role.')
    }
  }


  const calendarIntegrationsList = [
    {
      id: 'google',
      name: 'Google Calendar',
      description: 'Read-only sync of your Google Calendar events',
      icon: (
        <svg className="w-8 h-8" viewBox="0 0 24 24">
          <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
          <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
          <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
          <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
        </svg>
      ),
    },
    {
      id: 'microsoft',
      name: 'Microsoft Outlook',
      description: 'Read-only sync of your Outlook calendar events',
      icon: (
        <svg className="w-8 h-8" viewBox="0 0 24 24">
          <path fill="#0078D4" d="M24 7.387v10.478c0 .23-.08.424-.238.576-.158.152-.357.228-.598.228h-8.453v-6.085l1.604 1.17c.105.076.226.114.364.114.138 0 .259-.038.364-.114l6.955-5.082v-.095l-7.32 5.347-2.334-1.707V6.17h9.418c.242 0 .44.076.598.228.158.152.238.346.238.576v.413z" />
        </svg>
      ),
    },
  ]

  // Placeholder for future integrations (e.g. Teams, Google Meet). Empty until we support them.
  const otherIntegrationsList = []

  if (standalone) {
    return (
      <div className="min-h-screen flex flex-col bg-gray-50 relative overflow-hidden">
        <div className="pointer-events-none absolute inset-x-0 top-0 h-80 bg-[radial-gradient(circle_at_top,rgba(22,193,178,0.12),transparent_62%)]" />
        <div className="pointer-events-none absolute right-0 top-20 h-64 w-64 rounded-full bg-[#e4faf8] blur-3xl opacity-60" />

        {/* Standalone top bar */}
        <div className="flex items-center justify-between px-8 py-5 bg-white border-b border-gray-100 relative z-10">
          <Link to="/home" className="inline-flex items-center gap-2 text-sm font-medium text-gray-400 hover:text-gray-600 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            Back
          </Link>
          <Link to="/" className="flex items-center gap-3">
            <div className="w-10 h-9 flex items-center justify-center flex-shrink-0">
              <img src="/rithmo-mark.png" alt="Rithmo logo" className="w-full h-full object-contain" />
            </div>
            <span className="text-xl font-bold text-gray-900">Rithmo</span>
          </Link>
          <div className="w-16" />
        </div>

        <main className="flex-1 relative z-10 px-8 py-10">
          <div className="max-w-xl mx-auto">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Account Settings</h1>
                <p className="text-sm text-gray-400 mt-1">Profile, timezone, and security</p>
              </div>
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold text-white text-sm transition-opacity hover:opacity-90 disabled:opacity-60"
                style={{ background: TEAL_GRADIENT }}
              >
                {saving ? (
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : saved ? (
                  <Check className="w-4 h-4" />
                ) : (
                  <Save className="w-4 h-4" />
                )}
                {saving ? 'Saving…' : saved ? 'Saved!' : 'Save'}
              </button>
            </div>

            <div className="bg-white rounded-2xl border border-gray-100 p-8 shadow-sm space-y-8">

              {/* Avatar + name display */}
              <div className="flex items-center gap-5">
                <div className="relative">
                  <div className="avatar avatar-lg text-2xl overflow-hidden">
                    {profile.avatar ? (
                      <img src={profile.avatar} alt={`${profile.name || 'User'} avatar`} className="w-full h-full object-cover" />
                    ) : (
                      profile.name?.[0]?.toUpperCase() || 'U'
                    )}
                  </div>
                  <input ref={fileInputRef} type="file" accept="image/*" onChange={handleAvatarChange} className="hidden" />
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="absolute -bottom-1 -right-1 w-8 h-8 rounded-full flex items-center justify-center shadow-lg"
                    style={{ background: TEAL_GRADIENT }}
                    aria-label="Upload profile photo"
                  >
                    <Camera className="w-4 h-4 text-white" />
                  </button>
                </div>
                <div>
                  <p className="font-semibold text-gray-900">{profile.name || 'User'}</p>
                  <p className="text-sm text-gray-400">{profile.email}</p>
                </div>
              </div>

              {/* Fields */}
              <div className="space-y-5">
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1.5">Full Name</label>
                  <input
                    type="text"
                    value={profile.name}
                    onChange={(e) => setProfile({ ...profile, name: e.target.value })}
                    className="w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-gray-900 outline-none transition focus:border-[#16c1b2]/50 focus:bg-white focus:ring-4 focus:ring-[#16c1b2]/10"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1.5">Email</label>
                  <input
                    type="email"
                    value={profile.email}
                    readOnly
                    disabled
                    className="w-full rounded-xl border border-gray-200 bg-gray-100 px-4 py-3 text-gray-500 cursor-not-allowed"
                  />
                  <p className="text-xs text-gray-400 mt-1">Email cannot be changed.</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1.5">Timezone</label>
                  <select
                    value={profile.timezone || ''}
                    onChange={(e) => setProfile({ ...profile, timezone: e.target.value })}
                    className="w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-gray-900 outline-none transition focus:border-[#16c1b2]/50 focus:bg-white focus:ring-4 focus:ring-[#16c1b2]/10"
                  >
                    <option value="">(GMT+00:00) UTC (default)</option>
                    {timezoneOptions.map((row) => {
                      const suffix = row.value === getDetectedTimezone() ? ' — this device' : ''
                      return (
                        <option key={row.value} value={row.value}>{row.label}{suffix}</option>
                      )
                    })}
                  </select>
                  <p className="text-xs text-gray-400 mt-1">Used for meeting times in emails and scheduling.</p>
                </div>

                {profileError && <p className="text-sm text-red-500">{profileError}</p>}
              </div>

              {/* Zoom connection */}
              <div className="pt-6 border-t border-gray-100">
                <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <VideoIcon className="w-4 h-4" style={{ color: TEAL }} />
                  Zoom
                </h3>
                {zoomConnected ? (
                  <div className="flex items-center gap-3 px-4 py-3 rounded-xl bg-[#e4faf8] border border-[#16c1b2]/20">
                    <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: TEAL }} />
                    <p className="text-sm font-medium text-gray-700">Connected</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="flex items-center gap-3 px-4 py-3 rounded-xl bg-gray-50 border border-gray-200">
                      <div className="w-2 h-2 rounded-full bg-gray-300 flex-shrink-0" />
                      <p className="text-sm text-gray-500">Not connected</p>
                    </div>
                    <a
                      href={connectZoomUrl || '#'}
                      onClick={(e) => { if (!connectZoomUrl) e.preventDefault() }}
                      className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl font-semibold text-white text-sm transition-opacity hover:opacity-90"
                      style={{ background: TEAL_GRADIENT }}
                    >
                      <VideoIcon className="w-4 h-4" />
                      Connect Zoom Account
                    </a>
                  </div>
                )}
              </div>

              {/* Password */}
              <div className="pt-6 border-t border-gray-100">
                <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Lock className="w-4 h-4 text-gray-500" />
                  Change Password
                </h3>
                <div className="space-y-4">
                  {[
                    { label: 'Current Password', key: 'current' },
                    { label: 'New Password',      key: 'next' },
                    { label: 'Confirm Password',  key: 'confirm' },
                  ].map(({ label, key }) => (
                    <div key={key}>
                      <label className="block text-sm font-medium text-gray-600 mb-1.5">{label}</label>
                      <input
                        type="password"
                        value={passwordForm[key]}
                        onChange={(e) => setPasswordForm({ ...passwordForm, [key]: e.target.value })}
                        className="w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-gray-900 outline-none transition focus:border-[#16c1b2]/50 focus:bg-white focus:ring-4 focus:ring-[#16c1b2]/10"
                      />
                    </div>
                  ))}
                </div>
                {passwordError && <p className="text-sm text-red-500 mt-3">{passwordError}</p>}
                <button
                  type="button"
                  onClick={handlePasswordUpdate}
                  disabled={passwordSaving}
                  className="mt-4 flex items-center gap-2 px-4 py-2.5 rounded-xl border border-gray-200 bg-white text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-60"
                >
                  {passwordSaving ? (
                    <div className="w-4 h-4 border-2 border-gray-300 border-t-gray-700 rounded-full animate-spin" />
                  ) : passwordSaved ? (
                    <Check className="w-4 h-4 text-green-600" />
                  ) : (
                    <Lock className="w-4 h-4" />
                  )}
                  {passwordSaving ? 'Updating…' : passwordSaved ? 'Updated' : 'Update Password'}
                </button>
              </div>

            </div>
          </div>
        </main>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Sidebar />

      <main className="lg:ml-[280px] p-4 pt-20 lg:pt-8 lg:p-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex flex-wrap items-center justify-between gap-3 mb-8"
        >
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Settings</h1>
            <p className="text-gray-500">Manage your account and preferences</p>
          </div>
          {activeSection === 'profile' && (
            <button
              onClick={handleSave}
              disabled={saving}
              className="btn-primary flex items-center gap-2"
            >
              {saving ? (
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : saved ? (
                <Check className="w-5 h-5" />
              ) : (
                <Save className="w-5 h-5" />
              )}
              {saving ? 'Saving...' : saved ? 'Saved!' : 'Save Changes'}
            </button>
          )}
          {activeSection === 'notifications' && (
            <button
              onClick={handleNotificationsSave}
              disabled={notificationsSaving}
              className="btn-primary flex items-center gap-2"
            >
              {notificationsSaving ? (
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : notificationsSaved ? (
                <Check className="w-5 h-5" />
              ) : (
                <Save className="w-5 h-5" />
              )}
              {notificationsSaving ? 'Saving...' : notificationsSaved ? 'Saved!' : 'Save Changes'}
            </button>
          )}
          {/* Rithmo Customization save button — hidden while voice options are provider-specific
          {activeSection === 'rithmo-customization' && (
            <button
              onClick={handleCustomizationSave}
              disabled={customizationSaving}
              className="btn-primary flex items-center gap-2"
            >
              {customizationSaving ? (
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : customizationSaved ? (
                <Check className="w-5 h-5" />
              ) : (
                <Save className="w-5 h-5" />
              )}
              {customizationSaving ? 'Saving...' : customizationSaved ? 'Saved!' : 'Save Changes'}
            </button>
          )}
          */}
        </motion.div>

        <div className="flex flex-col gap-4 md:flex-row md:gap-8">
          {/* Settings Navigation */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="w-full md:w-64 md:flex-shrink-0"
          >
            <nav className="glass rounded-2xl p-2 md:p-4 flex overflow-x-auto gap-1 md:flex-col md:overflow-x-visible md:space-y-1">
              {sections.map((section) => (
                <button
                  key={section.id}
                  onClick={() => setActiveSection(section.id)}
                  className={`shrink-0 nav-link items-center text-left whitespace-nowrap md:w-full md:items-start md:whitespace-normal ${
                    activeSection === section.id ? 'active' : ''
                  }`}
                >
                  <span className="flex-shrink-0">{section.icon}</span>
                  <span className="min-w-0 leading-tight">
                    {section.label}
                  </span>
                </button>
              ))}
            </nav>
          </motion.div>

          {/* Settings Content */}
          <motion.div
            key={activeSection}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex-1"
          >
            {activeSection === 'profile' && (
              <div className="glass rounded-2xl p-8">
                <h2 className="text-xl font-semibold text-gray-900 mb-6">Profile Settings</h2>

                {/* Avatar */}
                <div className="flex items-center gap-6 mb-8">
                  <div className="relative">
                    <div className="avatar avatar-lg text-2xl overflow-hidden">
                      {profile.avatar ? (
                        <img
                          src={profile.avatar}
                          alt={`${profile.name || 'User'} avatar`}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        profile.name?.[0]?.toUpperCase() || 'U'
                      )}
                    </div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleAvatarChange}
                      className="hidden"
                    />
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="absolute -bottom-1 -right-1 w-8 h-8 rounded-full bg-gradient-primary flex items-center justify-center shadow-lg"
                      aria-label="Upload profile photo"
                    >
                      <Camera className="w-4 h-4 text-white" />
                    </button>
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">{profile.name || 'User'}</p>
                    <p className="text-sm text-gray-400">{profile.email}</p>
                  </div>
                </div>

                {/* Badges — permanent on profile */}
                <div className="mb-8">
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="text-sm font-medium text-gray-700 flex items-center gap-2">
                      <Award className="w-4 h-4" />
                      Your badges
                    </h3>
                    <AllBadgesTooltip />
                  </div>
                  <BadgesList badges={profileBadges} showCategory emptyMessage="Earn badges by building agendas and running great meetings." />
                </div>

                <div className="space-y-6">
                  <div>
                    <label className="block text-sm text-gray-500 mb-2">Full Name</label>
                    <input
                      type="text"
                      value={profile.name}
                      onChange={(e) => setProfile({ ...profile, name: e.target.value })}
                      className="input-field"
                    />
                  </div>

                  <div>
                    <label className="block text-sm text-gray-500 mb-2">Email Address</label>
                    <input
                      type="email"
                      value={profile.email}
                      readOnly
                      disabled
                      className="input-field bg-gray-50 text-gray-600 cursor-not-allowed"
                      aria-label="Email (read-only)"
                    />
                    <p className="text-xs text-gray-400 mt-1">
                      Email cannot be changed.
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm text-gray-500 mb-2">Timezone</label>
                    <select
                      value={profile.timezone || ''}
                      onChange={(e) => setProfile({ ...profile, timezone: e.target.value })}
                      className="input-field max-w-full"
                      aria-label="Timezone for meeting times in emails"
                    >
                      <option value="">(GMT+00:00) UTC (default)</option>
                      {timezoneOptions.map((row) => {
                        const detected = getDetectedTimezone()
                        const suffix = row.value === detected ? ' — this device' : ''
                        return (
                          <option key={row.value} value={row.value}>
                            {row.label}
                            {suffix}
                          </option>
                        )
                      })}
                    </select>
                    <p className="text-xs text-gray-400 mt-1">
                      Same style as Zoom: GMT offset updates for daylight saving. Labels use your region&apos;s name (e.g. Mountain Time). Meeting times in invitation emails use this timezone.
                    </p>
                  </div>

                  {profileError && (
                    <p className="text-sm text-red-500">{profileError}</p>
                  )}
                </div>

                <div className="mt-8 pt-8 border-t border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <Lock className="w-5 h-5" />
                    Change Password
                  </h3>
                  <div className="grid gap-4 md:grid-cols-3">
                    <div>
                      <label className="block text-sm text-gray-500 mb-2">Current Password</label>
                      <input
                        type="password"
                        value={passwordForm.current}
                        onChange={(e) => setPasswordForm({ ...passwordForm, current: e.target.value })}
                        className="input-field"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-gray-500 mb-2">New Password</label>
                      <input
                        type="password"
                        value={passwordForm.next}
                        onChange={(e) => setPasswordForm({ ...passwordForm, next: e.target.value })}
                        className="input-field"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-gray-500 mb-2">Confirm Password</label>
                      <input
                        type="password"
                        value={passwordForm.confirm}
                        onChange={(e) => setPasswordForm({ ...passwordForm, confirm: e.target.value })}
                        className="input-field"
                      />
                    </div>
                  </div>
                  {passwordError && (
                    <p className="text-sm text-red-500 mt-3">{passwordError}</p>
                  )}
                  <button
                    type="button"
                    onClick={handlePasswordUpdate}
                    disabled={passwordSaving}
                    className="mt-4 btn-secondary flex items-center gap-2"
                  >
                    {passwordSaving ? (
                      <div className="w-5 h-5 border-2 border-gray-400/40 border-t-gray-700 rounded-full animate-spin" />
                    ) : passwordSaved ? (
                      <Check className="w-5 h-5 text-green-600" />
                    ) : (
                      <Lock className="w-5 h-5" />
                    )}
                    {passwordSaving ? 'Updating...' : passwordSaved ? 'Updated' : 'Update Password'}
                  </button>
                </div>

                {/* Delete account option – commented out for now
                <div className="mt-8 pt-8 border-t border-gray-200">
                  <h3 className="text-lg font-semibold text-red-500 mb-4 flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5" />
                    Danger Zone
                  </h3>
                  <p className="text-sm text-gray-500 mb-3">
                    Account deletion requires administrator approval.
                  </p>
                  <button
                    type="button"
                    disabled={!isAdmin}
                    className={`btn-ghost text-red-500 hover:text-red-600 hover:bg-red-50 flex items-center gap-2 ${
                      !isAdmin ? 'opacity-40 cursor-not-allowed' : ''
                    }`}
                  >
                    <Trash2 className="w-5 h-5" />
                    Delete Account
                  </button>
                  {!isAdmin && (
                    <p className="text-xs text-gray-400 mt-2">
                      Ask an administrator to request account deletion.
                    </p>
                  )}
                </div>
                */}
              </div>
            )}

            {/* Rithmo Customization panel — hidden while voice options are provider-specific
            {activeSection === 'rithmo-customization' && (
              <div className="glass rounded-2xl p-8 max-w-2xl">
                <h2 className="text-xl font-semibold text-gray-900 mb-2 flex items-center gap-2">
                  <Sparkles className="w-6 h-6 text-primary-500" />
                  Rithmo Customization
                </h2>
                <p className="text-sm text-gray-500 mb-8">
                  Choose the voice used for Rithmo assistant speech (voice agenda and other in-dashboard audio).
                </p>
                <div className="space-y-2">
                  <label htmlFor="rithmo-tts-voice" className="block text-sm font-medium text-gray-700">
                    Assistant voice
                  </label>
                  <select
                    id="rithmo-tts-voice"
                    value={rithmoTtsVoice}
                    onChange={(e) => setRithmoTtsVoice(e.target.value)}
                    className="input-field max-w-md"
                  >
                    <optgroup label="Female">
                      {RITHMO_TTS_VOICE_OPTIONS.filter((o) => o.gender === 'female').map((o) => (
                        <option key={o.id} value={o.id}>
                          {o.label}
                        </option>
                      ))}
                    </optgroup>
                    <optgroup label="Male">
                      {RITHMO_TTS_VOICE_OPTIONS.filter((o) => o.gender === 'male').map((o) => (
                        <option key={o.id} value={o.id}>
                          {o.label}
                        </option>
                      ))}
                    </optgroup>
                  </select>
                  <p className="text-xs text-gray-400 mt-2 max-w-lg">
                    Voices are powered by Deepgram Aura-2. Your choice applies when you are signed in.
                  </p>
                </div>
                {customizationError && (
                  <p className="text-sm text-red-500 mt-4">{customizationError}</p>
                )}
              </div>
            )}
            */}

            {activeSection === 'notifications' && (
              <div className="glass rounded-2xl p-8">
                <h2 className="text-xl font-semibold text-gray-900 mb-6">Notification Preferences</h2>

                {notificationsLoading ? (
                  <div className="flex items-center gap-2 text-gray-500">
                    <div className="w-5 h-5 border-2 border-gray-400/40 border-t-gray-700 rounded-full animate-spin" />
                    Loading preferences…
                  </div>
                ) : (
                  <>
                    <div className="space-y-4">
                      {[
                        { id: 'meetingReminders', label: 'Meeting Reminders', desc: '15 minutes before meetings start' },
                        { id: 'meetingInvitations', label: 'Meeting Invitations', desc: 'Email when you create a meeting (confirmation + details)' },
                        { id: 'dailyDigest', label: 'Daily Meeting Summary', desc: 'Daily meeting summary sent every morning' },
                        { id: 'weeklyReport', label: 'Weekly Report', desc: 'Weekly analytics and insights' },
                        { id: 'postMeetingSummary', label: 'Post Meeting Summaries', desc: 'Email after each meeting with a summary' },
                        { id: 'marketingEmails', label: 'Product Updates', desc: 'News about new features and updates' },
                      ].map((item) => (
                        <div
                          key={item.id}
                          className="flex items-center justify-between p-4 rounded-xl bg-gray-50"
                        >
                          <div>
                            <p className="font-medium text-gray-900">{item.label}</p>
                            <p className="text-sm text-gray-400">{item.desc}</p>
                          </div>
                          <button
                            onClick={() =>
                              setNotifications({
                                ...notifications,
                                [item.id]: !notifications[item.id],
                              })
                            }
                            className={`toggle ${notifications[item.id] ? 'active' : ''}`}
                          />
                        </div>
                      ))}
                    </div>
                    {/* Test desktop notifications (hidden)
                    <div className="mt-6 pt-6 border-t border-gray-200">
                      <p className="text-sm text-gray-500 mb-2">Test desktop notifications</p>
                      <div className="flex flex-wrap gap-2">
                        <button
                          type="button"
                          onClick={async () => {
                            if (typeof Notification === 'undefined') {
                              alert('Desktop notifications are not supported in this browser.')
                              return
                            }
                            if (Notification.permission === 'denied') {
                              alert('Notifications are blocked. Allow them in your browser settings for this site.')
                              return
                            }
                            if (Notification.permission === 'default') {
                              const perm = await Notification.requestPermission()
                              if (perm !== 'granted') {
                                alert('Permission denied. You will only see in-app toasts for reminders.')
                                return
                              }
                            }
                            try {
                              const n = new Notification('Rithmo test', {
                                body: 'Meeting reminder test — if you see this, desktop notifications work.',
                                icon: '/favicon.ico',
                              })
                              n.onclick = () => {
                                window.focus()
                                n.close()
                              }
                            } catch (e) {
                              alert('Notification failed: ' + (e?.message ?? String(e)))
                            }
                          }}
                          className="btn-secondary text-sm"
                        >
                          Send test notification now
                        </button>
                        <button
                          type="button"
                          onClick={async (e) => {
                            if (typeof Notification === 'undefined') {
                              alert('Desktop notifications are not supported in this browser.')
                              return
                            }
                            if (Notification.permission === 'denied') {
                              alert('Notifications are blocked. Allow them in your browser settings for this site.')
                              return
                            }
                            if (Notification.permission === 'default') {
                              const perm = await Notification.requestPermission()
                              if (perm !== 'granted') {
                                alert('Permission denied.')
                                return
                              }
                            }
                            const btn = e.currentTarget
                            btn.disabled = true
                            btn.textContent = 'Firing in 5s…'
                            setTimeout(() => {
                              try {
                                const n = new Notification('Meeting starting soon', {
                                  body: 'Test Meeting starts in 15 minutes',
                                  icon: '/favicon.ico',
                                })
                                n.onclick = () => {
                                  window.focus()
                                  n.close()
                                }
                                btn.textContent = 'Fired!'
                              } catch (e) {
                                btn.textContent = 'Failed'
                                alert('Notification failed: ' + (e?.message ?? String(e)))
                              }
                              setTimeout(() => {
                                btn.disabled = false
                                btn.textContent = 'Test reminder in 5 seconds'
                              }, 2000)
                            }, 5000)
                          }}
                          className="btn-secondary text-sm"
                        >
                          Test reminder in 5 seconds
                        </button>
                      </div>
                    </div>
                    */}
                    {notificationsError && (
                      <p className="text-sm text-red-500 mt-4">{notificationsError}</p>
                    )}
                  </>
                )}
              </div>
            )}

            {activeSection === 'integrations' && (
              <div className="glass rounded-2xl p-8">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className="text-xl font-semibold text-gray-900">Integrations</h2>
                    <p className="text-sm text-gray-400">
                      Connect your calendars to import external meetings.
                    </p>
                  </div>
                  <button
                    onClick={fetchCalendarStatus}
                    className="btn-secondary flex items-center gap-2"
                    disabled={calendarLoading}
                  >
                    <RefreshCw className={`w-4 h-4 ${calendarLoading ? 'animate-spin' : ''}`} />
                    Refresh
                  </button>
                </div>

                <div className="mb-6">
                  <SlackHuddleConnect />
                </div>

                <div className="mb-6">
                  <ZoomAutoCaptureToggle />
                </div>

                {calendarError && (
                  <div className="mb-4 rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-600">
                    {calendarError}
                  </div>
                )}

                <div className="space-y-4">
                  {calendarIntegrationsList.map((integration) => {
                    const status = calendarStatus[integration.id] || {}
                    const isConnected = status.isConnected
                    const isConnecting = calendarConnecting[integration.id]
                    const isSyncing = calendarSyncing[integration.id]

                    return (
                      <div
                        key={integration.id}
                        className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 p-6 rounded-xl bg-gray-50 border border-gray-100"
                      >
                        <div className="flex items-center gap-4">
                          <div className="w-14 h-14 rounded-xl bg-white border border-gray-100 flex items-center justify-center">
                            {integration.icon}
                          </div>
                          <div>
                            <p className="font-semibold text-gray-900">{integration.name}</p>
                            <p className="text-sm text-gray-400">{integration.description}</p>
                            <div className="text-xs text-gray-500 mt-2">
                              {isConnected
                                ? `Connected as ${status.connectedEmail || 'account linked'}`
                                : 'Not connected'}
                            </div>
                            <div className="text-xs text-gray-400 mt-1">
                              Last sync: {formatTimestamp(status.lastSyncAt)}
                            </div>
                            {status.lastError && (
                              <div className="text-xs text-red-500 mt-1">
                                Last error: {status.lastError}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {isConnected && (
                            <button
                              onClick={() => handleCalendarSync(integration.id)}
                              className="btn-secondary flex items-center gap-2"
                              disabled={isSyncing}
                            >
                              <RefreshCw className={`w-4 h-4 ${isSyncing ? 'animate-spin' : ''}`} />
                              {isSyncing ? 'Syncing...' : 'Sync now'}
                            </button>
                          )}
                          <button
                            onClick={() =>
                              isConnected
                                ? handleCalendarDisconnect(integration.id)
                                : handleCalendarConnect(integration.id)
                            }
                            className={`px-6 py-2 rounded-lg font-medium transition-all ${
                              isConnected
                                ? 'bg-red-50 text-red-600 hover:bg-red-100'
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                            disabled={isConnecting}
                          >
                            {isConnected ? (
                              <span className="flex items-center gap-2">
                                <Trash2 className="w-4 h-4" />
                                Disconnect
                              </span>
                            ) : (
                              <span className="flex items-center gap-2">
                                {isConnecting ? 'Connecting...' : 'Connect'}
                                <ExternalLink className="w-4 h-4" />
                              </span>
                            )}
                          </button>
                        </div>
                      </div>
                    )
                  })}
                </div>

                <div className="mt-10">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Slack</h3>
                  {searchParams.get('slack') === 'connected' && (
                    <div className="mb-4 rounded-xl border border-green-100 bg-green-50 px-4 py-3 text-sm text-green-700">
                      Slack connected. You'll get commitment check-ins as direct messages.
                    </div>
                  )}
                  {searchParams.get('slack') === 'error' && (
                    <div className="mb-4 rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-600">
                      Could not connect Slack. Please try again.
                    </div>
                  )}
                  <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 p-6 rounded-xl bg-gray-50 border border-gray-100">
                    <div className="flex items-center gap-4">
                      <div className="w-14 h-14 rounded-xl bg-white border border-gray-100 flex items-center justify-center">
                        <Slack className="w-6 h-6 text-gray-700" />
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900">Slack</p>
                        <p className="text-sm text-gray-400">
                          Get one-tap commitment check-ins as Slack DMs. Connect if your Slack email
                          differs from your Rithmo email.
                        </p>
                        <div className="text-xs text-gray-500 mt-2">
                          {slackStatus.connected
                            ? `Connected${slackStatus.linkedAt ? ` ${formatTimestamp(slackStatus.linkedAt)}` : ''}`
                            : 'Not connected'}
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <button
                        onClick={handleSlackConnect}
                        className="px-6 py-2 rounded-lg font-medium transition-all bg-gray-100 text-gray-700 hover:bg-gray-200"
                        disabled={slackConnecting || slackStatus.available === false}
                      >
                        <span className="flex items-center gap-2">
                          {slackConnecting
                            ? 'Connecting...'
                            : slackStatus.connected
                            ? 'Reconnect'
                            : 'Connect Slack'}
                          <ExternalLink className="w-4 h-4" />
                        </span>
                      </button>
                    </div>
                  </div>
                  {slackStatus.available === false && (
                    <p className="text-xs text-gray-400 mt-2">
                      Slack connection is not configured for this workspace yet.
                    </p>
                  )}
                </div>

                {otherIntegrationsList.length > 0 && (
                  <div className="mt-10">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Other integrations</h3>
                    <div className="space-y-3">
                      {otherIntegrationsList.map((integration) => (
                        <div
                          key={integration.id}
                          className="flex items-center justify-between p-5 rounded-xl bg-gray-50 border border-gray-100"
                        >
                          <div className="flex items-center gap-4">
                            <div className="w-12 h-12 rounded-xl bg-white border border-gray-100 flex items-center justify-center">
                              {integration.icon}
                            </div>
                            <div>
                              <p className="font-semibold text-gray-900">{integration.name}</p>
                              <p className="text-sm text-gray-400">{integration.description}</p>
                            </div>
                          </div>
                          <span className="text-xs uppercase tracking-wide text-gray-400">
                            Coming soon
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {activeSection === 'organization' && (
              <div className="glass rounded-2xl p-8">
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-2">
                    <h2 className="text-xl font-semibold text-gray-900">Organization</h2>
                    {allOrgs.length > 1 && (
                      <select
                        value={org?.id ?? ''}
                        disabled={switchingOrg}
                        onChange={async (e) => {
                          const targetId = e.target.value
                          if (!targetId || targetId === org?.id) return
                          setSwitchingOrg(true)
                          try {
                            await switchOrg(targetId)
                            toast.success('Switched organization')
                          } catch {
                            toast.error('Failed to switch organization')
                          } finally {
                            setSwitchingOrg(false)
                          }
                        }}
                        className="text-sm border border-gray-200 rounded-lg px-3 py-1.5 bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400"
                      >
                        {allOrgs.map((o) => (
                          <option key={o.id} value={o.id}>
                            {decodeHtmlEntities(o.name)}{o.isPersonal ? ' (Personal)' : ''}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                  <p className="text-gray-500">
                    Manage organization access, roles, and invitations.
                  </p>
                </div>

                {orgLoading ? (
                  <div className="rounded-2xl border border-gray-100 bg-white/70 p-6">
                    <p className="text-sm text-gray-500">Loading…</p>
                  </div>
                ) : !org ? (
                  <div className="rounded-2xl border border-gray-100 bg-white/70 p-6">
                    <p className="text-sm font-semibold text-gray-900 mb-2">Create your organization</p>
                    <p className="text-sm text-gray-500 mb-4">
                      Enter a name for your organization to get started. You will be the admin and can invite members.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-3 max-w-md">
                      <input
                        type="text"
                        value={orgName}
                        onChange={(e) => setOrgName(e.target.value)}
                        placeholder="Organization name"
                        className="input-field flex-1"
                      />
                      <button
                        type="button"
                        onClick={handleCreateOrg}
                        disabled={creatingOrg || !orgName.trim()}
                        className="btn-primary"
                      >
                        {creatingOrg ? 'Creating…' : 'Create'}
                      </button>
                    </div>
                    {createOrgError && (
                      <p className="text-sm text-red-500 mt-3">{createOrgError}</p>
                    )}
                  </div>
                ) : org.isPersonal ? (
                  <div className="space-y-6">
                    <div className="rounded-2xl border border-gray-100 bg-white/70 p-6">
                      <p className="text-sm font-semibold text-gray-900 mb-1">Personal workspace</p>
                      <p className="text-sm text-gray-500 mb-4">
                        {decodeHtmlEntities(org.name)} is your private workspace. To invite teammates by email, create a company
                        organization first. Your personal workspace stays available.
                      </p>
                    </div>
                    <div className="rounded-2xl border border-teal-100 bg-teal-50/40 p-6">
                      <p className="text-sm font-semibold text-gray-900 mb-2">Create a company organization</p>
                      <p className="text-sm text-gray-600 mb-4">
                        You will become the org admin and can send invitations from this page.
                      </p>
                      <div className="flex flex-col sm:flex-row gap-3 max-w-md">
                        <input
                          type="text"
                          value={orgName}
                          onChange={(e) => setOrgName(e.target.value)}
                          placeholder="Company or team name"
                          className="input-field flex-1"
                        />
                        <button
                          type="button"
                          onClick={handleCreateOrg}
                          disabled={creatingOrg || !orgName.trim()}
                          className="btn-primary"
                        >
                          {creatingOrg ? 'Creating…' : 'Create'}
                        </button>
                      </div>
                      {createOrgError && (
                        <p className="text-sm text-red-500 mt-3">{createOrgError}</p>
                      )}
                    </div>
                  </div>
                ) : (
                  <>
                    {orgActionError && (
                      <p className="text-sm text-red-500 mb-4">{orgActionError}</p>
                    )}
                    <div className="rounded-2xl border border-gray-100 bg-white/70 p-6 mb-6">
                      <p className="text-sm font-semibold text-gray-900 mb-1">{decodeHtmlEntities(org.name)}</p>
                      <p className="text-xs text-gray-400 mb-4">
                        Org-level roles: Admin can invite and manage members; Member can view.
                      </p>
                      <p className="text-xs text-gray-400 mb-4">
                        {orgMembers.length} member{orgMembers.length !== 1 ? 's' : ''}
                        {pendingInvitations.length > 0 && `, ${pendingInvitations.length} pending invite${pendingInvitations.length !== 1 ? 's' : ''}`}
                      </p>
                      {inviteeNotice && (
                        <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900 flex gap-2 items-start">
                          <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                          <span>{inviteeNotice}</span>
                        </div>
                      )}
                      {membersLoading ? (
                        <p className="text-sm text-gray-500">Loading…</p>
                      ) : (
                        <div className="space-y-4">
                          {orgMembers.map((member) => (
                            <div
                              key={member.id}
                              className="flex flex-col md:flex-row md:items-center md:justify-between gap-4"
                            >
                              <div>
                                <p className="font-medium text-gray-900">
                                  {decodeHtmlEntities(member.name)}
                                  {member.isCurrentUser ? ' (You)' : ''}
                                </p>
                                <p className="text-sm text-gray-400">{member.email}</p>
                              </div>
                              <div className="flex items-center gap-3">
                                {isAdmin && !member.isCurrentUser ? (
                                  <div className="flex items-center gap-2">
                                    <select
                                      value={member.role}
                                      onChange={(e) => handleSetOrgMemberRole(member.id, e.target.value)}
                                      className="input-field py-1 text-xs w-28"
                                    >
                                      <option value="org_admin">Org Admin</option>
                                      <option value="member">Org Member</option>
                                    </select>
                                    <button
                                      type="button"
                                      onClick={() => handleRemoveOrgMember(member.id)}
                                      className="btn-ghost text-red-500 hover:text-red-600 hover:bg-red-50"
                                    >
                                      Remove
                                    </button>
                                  </div>
                                ) : (
                                  <span
                                    className={
                                      member.role === 'org_admin'
                                        ? 'badge-primary'
                                        : 'badge bg-gray-100 text-gray-600 border border-gray-200'
                                    }
                                  >
                                    {member.role === 'org_admin' ? 'Org Admin' : 'Org Member'}
                                  </span>
                                )}
                              </div>
                            </div>
                          ))}
                          {pendingInvitations.map((inv) => (
                            <div
                              key={inv.id}
                              className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 opacity-90"
                            >
                              <div>
                                <p className="font-medium text-gray-700">{inv.email}</p>
                                <p className="text-xs text-amber-600 font-medium">Pending invitation</p>
                              </div>
                              <div className="flex items-center gap-3">
                                <span className="badge bg-amber-50 text-amber-700 border border-amber-200">
                                  Pending
                                </span>
                                <span className="text-xs text-gray-500 capitalize">{inv.role}</span>
                                {isAdmin && (
                                  <>
                                    <button
                                      type="button"
                                      onClick={() => handleSendInviteReminder(inv.inviteId, inv.email)}
                                      disabled={reminderSendingId === inv.inviteId || revokingInviteId === inv.inviteId}
                                      className="btn-secondary text-sm flex items-center gap-2"
                                    >
                                      {reminderSendingId === inv.inviteId ? (
                                        <div className="w-4 h-4 border-2 border-gray-400/40 border-t-gray-700 rounded-full animate-spin" />
                                      ) : (
                                        <RefreshCw className="w-4 h-4" />
                                      )}
                                      Send reminder
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => handleRevokeInvitation(inv.inviteId)}
                                      disabled={revokingInviteId === inv.inviteId || reminderSendingId === inv.inviteId}
                                      className="btn-ghost text-sm text-red-600 hover:text-red-700 hover:bg-red-50 flex items-center gap-1"
                                      title="Revoke invitation"
                                    >
                                      {revokingInviteId === inv.inviteId ? (
                                        <div className="w-4 h-4 border-2 border-red-200 border-t-red-600 rounded-full animate-spin" />
                                      ) : (
                                        <Trash2 className="w-4 h-4" />
                                      )}
                                      Revoke
                                    </button>
                                  </>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {isAdmin && !org?.isPersonal && (
                      <div className="rounded-2xl border border-gray-100 bg-white/70 p-6">
                        <p className="text-sm font-semibold text-gray-900 mb-4">Invite a teammate</p>
                        <div className="flex flex-col sm:flex-row gap-3 max-w-md">
                          <input
                            type="email"
                            value={inviteForm.email}
                            onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                            placeholder="Email address"
                            className="input-field flex-1"
                            disabled={inviting}
                          />
                          <button
                            type="button"
                            onClick={handleInvite}
                            disabled={inviting}
                            className="btn-primary"
                          >
                            {inviting ? 'Sending…' : 'Send invite'}
                          </button>
                        </div>
                        {inviteSuccess && (
                          <p className="text-sm text-green-600 mt-3">{inviteSuccess}</p>
                        )}
                        {inviteError && (
                          <p className="text-sm text-red-500 mt-3">{inviteError}</p>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {activeSection === 'teams' && (
              <div className="glass rounded-2xl p-8">
                <div className="mb-6">
                  <h2 className="text-xl font-semibold text-gray-900 mb-2">Teams</h2>
                  <p className="text-gray-500">
                    Create teams and add team members. All members of a team share team meeting data.
                    Team-level roles: Admin can manage the team; Member can view shared meetings.
                  </p>
                </div>
                {teamActionError && (
                  <p className="text-sm text-red-500 mb-4">{teamActionError}</p>
                )}
                <div className="rounded-2xl border border-gray-100 bg-white/70 p-6 mb-6">
                  <p className="text-sm font-semibold text-gray-900 mb-4">Create a team</p>
                  <div className="flex flex-col sm:flex-row gap-3 max-w-md">
                    <input
                      type="text"
                      value={newTeamName}
                      onChange={(e) => setNewTeamName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key !== 'Enter') return
                        if (creatingTeam) return
                        if (!(newTeamName ?? '').trim()) return
                        e.preventDefault()
                        handleCreateTeam()
                      }}
                      placeholder="Team name"
                      className="input-field flex-1"
                    />
                    <input
                      type="color"
                      value={newTeamColor}
                      onChange={(e) => setNewTeamColor(e.target.value)}
                      className="h-[44px] w-[56px] rounded-lg border border-gray-200 bg-white px-1"
                      aria-label="New team color"
                      title="Team color"
                    />
                    <button
                      type="button"
                      onClick={handleCreateTeam}
                      disabled={creatingTeam || !newTeamName.trim()}
                      className="btn-primary"
                    >
                      {creatingTeam ? 'Creating…' : 'Create'}
                    </button>
                  </div>
                  {createTeamError && (
                    <p className="text-sm text-red-500 mt-3">{createTeamError}</p>
                  )}
                </div>
                {teamsLoading ? (
                  <p className="text-sm text-gray-500">Loading teams…</p>
                ) : teams.length === 0 ? (
                  <p className="text-sm text-gray-500">No teams yet. Create one above.</p>
                ) : (
                  <div className="space-y-4">
                    {teams.map((team) => (
                      <div
                        key={team.id}
                        className="rounded-2xl border border-gray-100 bg-white/70 p-6"
                      >
                        <div className="flex items-center justify-between gap-4">
                          <button
                            type="button"
                            onClick={() => handleLoadTeamDetail(team.id)}
                            className="text-left flex-1 min-w-0"
                          >
                            <div className="flex items-center gap-2">
                              <span
                                className="w-3 h-3 rounded-full border border-gray-200"
                                style={{ backgroundColor: team.color || '#64748b' }}
                              />
                              <p className="font-semibold text-gray-900">{decodeHtmlEntities(team.name)}</p>
                            </div>
                            <p className="text-sm text-gray-500">
                              {team.memberCount} member{team.memberCount !== 1 ? 's' : ''}
                              {team.myRole ? ` · You: ${team.myRole}` : ''}
                            </p>
                          </button>
                          {team.myRole === 'admin' && (
                            <button
                              type="button"
                              onClick={() => handleArchiveTeam(team.id)}
                              className="btn-ghost text-red-500 hover:text-red-600 text-sm"
                            >
                              Archive
                            </button>
                          )}
                        </div>
                        {expandedTeamId === team.id && teamDetail?.team?.id === team.id && (
                          <div className="mt-4 pt-4 border-t border-gray-100">
                            <div className="flex items-center justify-between gap-3 mb-4">
                              <div className="min-w-0">
                                <p className="text-sm font-medium text-gray-700">Team color</p>
                                <p className="text-xs text-gray-400">Used for dashboard badges</p>
                              </div>
                              {teamDetail.team?.myRole === 'admin' ? (
                                <input
                                  type="color"
                                  value={teamDetail.team?.color || '#64748b'}
                                  onChange={(e) => {
                                    const next = e.target.value
                                    setTeamDetail((prev) => prev ? ({ ...prev, team: { ...prev.team, color: next } }) : prev)
                                    handleUpdateTeamColor(team.id, next)
                                  }}
                                  className="h-[40px] w-[56px] rounded-lg border border-gray-200 bg-white px-1"
                                  aria-label="Team color"
                                  title="Team color"
                                />
                              ) : (
                                <div className="flex items-center gap-2 text-sm text-gray-600">
                                  <span
                                    className="w-3 h-3 rounded-full border border-gray-200"
                                    style={{ backgroundColor: teamDetail.team?.color || '#64748b' }}
                                  />
                                  <span>{teamDetail.team?.color || '#64748b'}</span>
                                </div>
                              )}
                            </div>
                            <p className="text-sm font-medium text-gray-700 mb-2">Members</p>
                            <ul className="space-y-2 mb-4">
                              {(teamDetail.members ?? []).map((m) => (
                                <li
                                  key={m.userId}
                                  className="flex items-center justify-between gap-2 text-sm"
                                >
                                  <span className="text-gray-900">
                                    {m.fullName || m.email}
                                    {m.role === 'admin' ? ' (admin)' : ''}
                                  </span>
                                  {(() => {
                                    const currentUserId = user?.id
                                    const isSelf = String(m.userId) === String(currentUserId)
                                    const canManageMember = teamDetail.team?.myRole === 'admin' && !isSelf
                                    return canManageMember ? (
                                    <div className="flex items-center gap-2">
                                      <select
                                        value={m.role}
                                        onChange={(e) => handleSetTeamMemberRole(team.id, m.userId, e.target.value)}
                                        data-debug-role-select="true"
                                        className="input-field py-1 text-xs w-28"
                                      >
                                        <option value="member">Member</option>
                                        <option value="admin">Admin</option>
                                      </select>
                                      <button
                                        type="button"
                                        onClick={() => handleRemoveTeamMember(team.id, m.userId)}
                                        className="btn-ghost text-red-500 text-xs"
                                      >
                                        Remove
                                      </button>
                                    </div>
                                    ) : null
                                  })()}
                                </li>
                              ))}
                            </ul>
                            {teamDetail.team?.myRole === 'admin' && (
                              <div className="flex flex-wrap gap-2 items-center">
                                <select
                                  value={addMemberTeamId === team.id ? addMemberUserId : ''}
                                  onChange={(e) => {
                                    setAddMemberTeamId(team.id)
                                    setAddMemberUserId(e.target.value)
                                  }}
                                  className="input-field py-2 text-sm min-w-[180px]"
                                >
                                  <option value="">Add a member…</option>
                                  {(orgMembers ?? [])
                                    .filter((m) => !(teamDetail.members ?? []).some((tm) => String(tm.userId) === String(m.id)))
                                    .map((m) => (
                                      <option key={m.id} value={m.id}>
                                        {decodeHtmlEntities(m.name)} ({m.email})
                                      </option>
                                    ))}
                                </select>
                                {addMemberTeamId === team.id && addMemberUserId && (
                                  <button
                                    type="button"
                                    onClick={handleAddTeamMember}
                                    className="btn-primary text-sm"
                                  >
                                    Add
                                  </button>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {activeSection === 'plans' && (
              <PlansSection
                currentPlan={currentPlan}
                setCurrentPlan={setCurrentPlan}
                planLoading={planLoading}
                setPlanLoading={setPlanLoading}
                checkoutLoading={checkoutLoading}
                setCheckoutLoading={setCheckoutLoading}
                toast={toast}
              />
            )}

          </motion.div>
        </div>
      </main>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Plans Section
// ---------------------------------------------------------------------------

function PlansSection({ currentPlan, setCurrentPlan, planLoading, setPlanLoading, checkoutLoading, setCheckoutLoading, toast }) {
  const [plans, setPlans] = useState([])
  const [invoices, setInvoices] = useState([])
  const [invoicesLoading, setInvoicesLoading] = useState(false)
  const billing = useBilling()

  // Load plans from DB + current subscription
  useEffect(() => {
    setPlanLoading(true)
    Promise.all([
      stripeAPI.getPlans().catch(() => ({ data: { plans: [] } })),
      stripeAPI.getSubscription().catch(() => ({ data: { plan: 'free' } })),
    ]).then(([plansRes, subRes]) => {
      const p = plansRes?.data?.plans ?? plansRes?.plans ?? []
      setPlans(Array.isArray(p) ? p : [])
      const sub = subRes?.data ?? subRes
      setCurrentPlan(sub?.plan || 'free')
    }).finally(() => setPlanLoading(false))
  }, [setCurrentPlan, setPlanLoading])

  // Load invoices
  useEffect(() => {
    setInvoicesLoading(true)
    stripeAPI.getInvoices()
      .then((res) => setInvoices((res?.data?.invoices ?? res?.invoices) || []))
      .catch(() => setInvoices([]))
      .finally(() => setInvoicesLoading(false))
  }, [])

  // Handle post-checkout redirect
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const checkout = params.get('checkout')
    if (checkout === 'success') {
      toast.success('Subscription activated', 'Your plan has been upgraded successfully.')
      stripeAPI.getSubscription()
        .then((res) => setCurrentPlan((res?.data ?? res)?.plan || 'free'))
        .catch(() => {})
      billing.refresh()
    } else if (checkout === 'canceled') {
      toast.error('Checkout canceled', 'You can try again anytime.')
    }
    if (checkout) {
      params.delete('checkout')
      const newSearch = params.toString()
      window.history.replaceState(null, '', window.location.pathname + (newSearch ? `?${newSearch}` : ''))
    }
  }, [toast, setCurrentPlan, billing])

  const handleSubscribe = async (planKey) => {
    setCheckoutLoading(planKey)
    try {
      const res = await stripeAPI.createCheckoutSession(planKey)
      const url = res?.data?.url ?? res?.url
      if (url) {
        window.location.href = url
      } else {
        toast.error('Checkout failed', 'Could not create checkout session.')
      }
    } catch (err) {
      toast.error('Checkout failed', err?.message || 'Something went wrong.')
    } finally {
      setCheckoutLoading(null)
    }
  }

  const handleUpgrade = async (planKey) => {
    setCheckoutLoading(planKey)
    try {
      const res = await stripeAPI.upgrade(planKey)
      const data = res?.data ?? res
      toast.success('Plan upgraded', data?.message || 'Prorated charges applied.')
      setCurrentPlan(planKey)
      billing.refresh()
    } catch (err) {
      toast.error('Upgrade failed', err?.message || 'Something went wrong.')
    } finally {
      setCheckoutLoading(null)
    }
  }

  const handleDowngrade = async (planKey) => {
    setCheckoutLoading(planKey)
    try {
      const res = await stripeAPI.downgrade(planKey)
      const data = res?.data ?? res
      toast.success('Downgrade scheduled', data?.message || 'Takes effect next billing cycle.')
    } catch (err) {
      toast.error('Downgrade failed', err?.message || 'Something went wrong.')
    } finally {
      setCheckoutLoading(null)
    }
  }

  const PLAN_ORDER = { team: 1, growth: 2, enterprise: 3 }
  const currentPlanOrder = PLAN_ORDER[currentPlan] || 0

  const formatPrice = (cents) => {
    return `$${(cents / 100).toLocaleString()}`
  }

  return (
    <div className="space-y-6">
      <div className="glass rounded-2xl p-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Plans</h2>
        <p className="text-gray-500 mb-6">
          Choose the plan that fits your team. You can upgrade or change plans at any time.
        </p>

        {/* Current plan + usage */}
        <div className="mb-8 rounded-xl border border-primary-200 bg-primary-50 px-5 py-4">
          <div className="flex items-center gap-3">
            <div className="w-2.5 h-2.5 rounded-full bg-primary-500" />
            <span className="text-sm font-medium text-primary-900">
              Current plan: <span className="font-bold capitalize">{planLoading ? '...' : currentPlan}</span>
            </span>
          </div>
          {billing.enforced && billing.usage && billing.usage.includedMinutes > 0 && (
            <div className="mt-3">
              <div className="flex justify-between text-xs text-primary-700 mb-1">
                <span>{Math.round(billing.usage.usedMinutes / 60)}h used</span>
                <span>{Math.round(billing.usage.remainingMinutes / 60)}h remaining of {Math.round(billing.usage.includedMinutes / 60)}h</span>
              </div>
              <div className="h-2 w-full rounded-full bg-primary-200 overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all ${
                    billing.usage.remainingMinutes / billing.usage.includedMinutes < 0.15 ? 'bg-red-500'
                    : billing.usage.remainingMinutes / billing.usage.includedMinutes < 0.3 ? 'bg-amber-500'
                    : 'bg-primary-600'
                  }`}
                  style={{ width: `${Math.min(100, (billing.usage.usedMinutes / Math.max(1, billing.usage.includedMinutes)) * 100)}%` }}
                />
              </div>
            </div>
          )}
        </div>

        {/* Plan cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {plans.map((plan) => {
            const isCurrentPlan = currentPlan === plan.plan_key
            const isLoading = checkoutLoading === plan.plan_key
            const planOrder = PLAN_ORDER[plan.plan_key] || 0
            const isUpgrade = currentPlanOrder > 0 && planOrder > currentPlanOrder
            const isDowngrade = currentPlanOrder > 0 && planOrder < currentPlanOrder
            const isNewSubscription = currentPlan === 'free'
            const features = Array.isArray(plan.features) ? plan.features : JSON.parse(plan.features || '[]')

            return (
              <div
                key={plan.plan_key}
                className={`flex flex-col rounded-2xl border-2 p-6 transition-all ${
                  isCurrentPlan ? 'border-primary-400 ring-2 ring-primary-500 ring-offset-2' : 'border-gray-200 hover:border-primary-200'
                }`}
              >
                <div className="mb-4">
                  <span className="inline-block rounded-lg bg-primary-500 px-4 py-1.5 text-sm font-semibold text-white">
                    {plan.name}
                  </span>
                </div>

                <div className="mb-5">
                  <p className="text-sm font-semibold text-gray-700">Platform Fee:</p>
                  <p className="text-3xl font-bold text-gray-900">
                    {formatPrice(plan.monthly_price_cents)}<span className="text-base font-medium text-gray-500">/mo</span>
                  </p>
                </div>

                <ul className="space-y-2.5 mb-6 flex-1">
                  {features.map((feature, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                      <span className="mt-0.5 shrink-0">•</span>
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>

                {isCurrentPlan ? (
                  <button disabled className="w-full rounded-xl py-2.5 text-sm font-semibold text-primary-700 bg-primary-100 cursor-default">
                    Current Plan
                  </button>
                ) : isNewSubscription ? (
                  <button
                    onClick={() => handleSubscribe(plan.plan_key)}
                    disabled={isLoading || planLoading}
                    className="btn-primary w-full text-sm disabled:opacity-60"
                  >
                    {isLoading ? 'Redirecting...' : 'Subscribe'}
                  </button>
                ) : isUpgrade ? (
                  <button
                    onClick={() => handleUpgrade(plan.plan_key)}
                    disabled={isLoading || planLoading}
                    className="btn-primary w-full text-sm disabled:opacity-60"
                  >
                    {isLoading ? 'Processing...' : 'Upgrade'}
                  </button>
                ) : isDowngrade ? (
                  <button
                    onClick={() => handleDowngrade(plan.plan_key)}
                    disabled={isLoading || planLoading}
                    className="w-full rounded-xl py-2.5 text-sm font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 disabled:opacity-60"
                  >
                    {isLoading ? 'Processing...' : 'Downgrade'}
                  </button>
                ) : null}
              </div>
            )
          })}
        </div>
      </div>

      {/* Invoice History */}
      <div className="glass rounded-2xl p-8">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Billing History</h3>
        {invoicesLoading ? (
          <p className="text-sm text-gray-500">Loading invoices...</p>
        ) : invoices.length === 0 ? (
          <p className="text-sm text-gray-500">No invoices yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="text-left py-2 text-gray-500 font-medium">Date</th>
                  <th className="text-left py-2 text-gray-500 font-medium">Invoice</th>
                  <th className="text-left py-2 text-gray-500 font-medium">Amount</th>
                  <th className="text-left py-2 text-gray-500 font-medium">Status</th>
                  <th className="text-right py-2 text-gray-500 font-medium"></th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((inv) => (
                  <tr key={inv.id} className="border-b border-gray-50">
                    <td className="py-3 text-gray-700">{new Date(inv.created).toLocaleDateString()}</td>
                    <td className="py-3 text-gray-700">{inv.number || '—'}</td>
                    <td className="py-3 text-gray-900 font-medium">{formatPrice(inv.amountPaid)} {inv.currency?.toUpperCase()}</td>
                    <td className="py-3">
                      <span className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${
                        inv.status === 'paid' ? 'bg-green-100 text-green-700' :
                        inv.status === 'open' ? 'bg-amber-100 text-amber-700' :
                        'bg-gray-100 text-gray-600'
                      }`}>
                        {inv.status}
                      </span>
                    </td>
                    <td className="py-3 text-right">
                      {inv.hostedInvoiceUrl && (
                        <a href={inv.hostedInvoiceUrl} target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:underline text-xs">
                          View
                        </a>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}

export default SettingsPage
